import * as K from 'common/constants';
import { UrlParamSet } from 'common/types';

/**
 * There are three classifications of URL parameters:
 *
 * 1. Event params require data to be fetched.
 * 2. View params affect how data is displayed and persist across queries
 * 3. Disposable params affect how data is displayed and do not persist across queries
 *
 * Most of these are supported by the legacy app. Some of them are new. Supported legacy params
 * are documented at https://www.notion.so/scalyr/URL-parameters-5f08c59479484c3eb4985d19bf6faacfs
 */

const UrlParamsByRoute = {} as Record<K.ROUTE, UrlParamSet>;

// initialize every route with empty param lists
Object.values(K.ROUTE).forEach((route) => {
  UrlParamsByRoute[route] = {
    eventParamList: K.EMPTY_ARRAY,
    viewParamList: K.EMPTY_ARRAY,
    disposableParamList: K.EMPTY_ARRAY,
  };
});

// Search logs page

UrlParamsByRoute[K.ROUTE.EVENTS].eventParamList = [
  K.URL_PARAM.DATA_SOURCE,
  K.URL_PARAM.END_TIME,
  K.URL_PARAM.FILTER,
  K.URL_PARAM.LOG,
  K.URL_PARAM.LOG_SOURCE,
  K.URL_PARAM.KUBERNETES_CLUSTER,
  K.URL_PARAM.KUBERNETES_CONTROLLER,
  K.URL_PARAM.KUBERNETES_CRON_JOB,
  K.URL_PARAM.KUBERNETES_DAEMON_SET,
  K.URL_PARAM.KUBERNETES_DEPLOYMENT,
  K.URL_PARAM.KUBERNETES_JOB,
  K.URL_PARAM.KUBERNETES_KIND,
  K.URL_PARAM.KUBERNETES_REPLICA_SET,
  K.URL_PARAM.KUBERNETES_REPLICATION_CONTROLLER,
  K.URL_PARAM.KUBERNETES_STATEFUL_SET,
  K.URL_PARAM.RESULTS_TAB,
  K.URL_PARAM.START_TIME,
  K.URL_PARAM.TEAM_EMAILS,
  K.URL_PARAM.VIEW,
];

UrlParamsByRoute[K.ROUTE.EVENTS].disposableParamList = [
  K.URL_PARAM.ADDITIONAL_FIELDS,
  K.URL_PARAM.CENTERED,
  K.URL_PARAM.CONTINUATION_TOKEN,
  K.URL_PARAM.DISPLAY_MESSAGE,
  K.URL_PARAM.DISPLAY_MODE,
  K.URL_PARAM.HIGHLIGHT_EVENT,
  K.URL_PARAM.INCLUSIVE_START,
  K.URL_PARAM.SYSTEM_PREFS,
  K.URL_PARAM.TOKEN_CENTERED,
];

// Search graph page

UrlParamsByRoute[K.ROUTE.GRAPH].eventParamList = [
  K.URL_PARAM.BAR_WIDTH,
  K.URL_PARAM.BREAKDOWN_FACET,
  K.URL_PARAM.COMPARE_TIME_OFFSET,
  K.URL_PARAM.DATA_SOURCE,
  K.URL_PARAM.END_TIME,
  K.URL_PARAM.FACET,
  K.URL_PARAM.FILTER,
  K.URL_PARAM.GRAPH_STYLE,
  K.URL_PARAM.KUBERNETES_CLUSTER,
  K.URL_PARAM.KUBERNETES_CONTROLLER,
  K.URL_PARAM.KUBERNETES_CRON_JOB,
  K.URL_PARAM.KUBERNETES_DAEMON_SET,
  K.URL_PARAM.KUBERNETES_DEPLOYMENT,
  K.URL_PARAM.KUBERNETES_JOB,
  K.URL_PARAM.KUBERNETES_KIND,
  K.URL_PARAM.KUBERNETES_REPLICA_SET,
  K.URL_PARAM.KUBERNETES_REPLICATION_CONTROLLER,
  K.URL_PARAM.KUBERNETES_STATEFUL_SET,
  K.URL_PARAM.LOG,
  K.URL_PARAM.LOG_SOURCE,
  K.URL_PARAM.NUM_BARS,
  K.URL_PARAM.POST_GRAPH_TO_SLACK_OPEN,
  K.URL_PARAM.START_TIME,
  K.URL_PARAM.STYLE,
  K.URL_PARAM.TEAM_EMAILS,
  K.URL_PARAM.VIEW,
];

UrlParamsByRoute[K.ROUTE.GRAPH].viewParamList = [
  K.URL_PARAM.LINE_SMOOTHING,
  K.URL_PARAM.Y_SCALE,
  K.URL_PARAM.HIDE_GRAPHS,
  K.URL_PARAM.SHOW_GRAPHS,
];

// Distribution graph page
UrlParamsByRoute[K.ROUTE.DISTRIBUTION].eventParamList = [
  K.URL_PARAM.END_TIME,
  K.URL_PARAM.FACET,
  K.URL_PARAM.FILTER,
  K.URL_PARAM.KUBERNETES_CLUSTER,
  K.URL_PARAM.KUBERNETES_CONTROLLER,
  K.URL_PARAM.KUBERNETES_CRON_JOB,
  K.URL_PARAM.KUBERNETES_DAEMON_SET,
  K.URL_PARAM.KUBERNETES_DEPLOYMENT,
  K.URL_PARAM.KUBERNETES_JOB,
  K.URL_PARAM.KUBERNETES_KIND,
  K.URL_PARAM.KUBERNETES_REPLICA_SET,
  K.URL_PARAM.KUBERNETES_REPLICATION_CONTROLLER,
  K.URL_PARAM.KUBERNETES_STATEFUL_SET,
  K.URL_PARAM.LOG,
  K.URL_PARAM.LOG_SOURCE,
  K.URL_PARAM.START_TIME,
  K.URL_PARAM.TEAM_EMAILS,
  K.URL_PARAM.VIEW,
];

// Live tail page
UrlParamsByRoute[K.ROUTE.LIVE_TAIL].eventParamList = [K.URL_PARAM.FILTER, K.URL_PARAM.VIEW];

// Overview page
UrlParamsByRoute[K.ROUTE.LOG_START].eventParamList = [K.URL_PARAM.TAB, K.URL_PARAM.SHOW_OLD];

// Dashboard page
UrlParamsByRoute[K.ROUTE.DASHBOARD].eventParamList = [
  K.URL_PARAM.DASHBOARD_NAME,
  K.URL_PARAM.FILTER,
  K.URL_PARAM.START_TIME,
  K.URL_PARAM.END_TIME,
  K.URL_PARAM.DASHBOARD_PARAMS,
  K.URL_PARAM.DASHBOARD_FILTERS,
  K.URL_PARAM.DASHBOARD_VIEW_GRAPH,
  K.URL_PARAM.DASHBOARD_ACTIVE_TAB,
  K.URL_PARAM.VIEW,
];

UrlParamsByRoute[K.ROUTE.DASHBOARD_LIST].eventParamList = [K.URL_PARAM.DASHBOARD_FILTER, K.URL_PARAM.VIEW];

// Power queries page
UrlParamsByRoute[K.ROUTE.POWER_QUERY].eventParamList = [
  K.URL_PARAM.FILTER,
  K.URL_PARAM.START_TIME,
  K.URL_PARAM.END_TIME,
  K.URL_PARAM.GRAPH_STYLE,
  K.URL_PARAM.TEAM_EMAILS,
  K.URL_PARAM.VIEW,
];

UrlParamsByRoute[K.ROUTE.POWER_QUERY].viewParamList = [
  K.URL_PARAM.MAX_PIE_SLICES,
  K.URL_PARAM.PIE_STYLE,
  K.URL_PARAM.LINE_SMOOTHING,
  K.URL_PARAM.Y_SCALE,
  K.URL_PARAM.HIDE_GRAPHS,
  K.URL_PARAM.SHOW_GRAPHS,
];

// Alert details page
UrlParamsByRoute[K.ROUTE.ALERT].eventParamList = [K.URL_PARAM.ID, K.URL_PARAM.VIEW];

// Manage Teams page
UrlParamsByRoute[K.ROUTE.MANAGE_TEAMS].eventParamList = [K.URL_PARAM.ORIGINAL_URL];

// Parser page
UrlParamsByRoute[K.ROUTE.PARSER].eventParamList = [K.URL_PARAM.PARSER, K.URL_PARAM.VIEW];

// Monitors page
UrlParamsByRoute[K.ROUTE.MONITORS_PAGE].eventParamList = [K.URL_PARAM.START_TIME, K.URL_PARAM.END_TIME];

// File page
UrlParamsByRoute[K.ROUTE.CONFIG_FILE].eventParamList = [K.URL_PARAM.PATH];

// Bulk export page
UrlParamsByRoute[K.ROUTE.BULK_EXPORTS].eventParamList = [
  K.URL_PARAM.FILTER,
  K.URL_PARAM.START_TIME,
  K.URL_PARAM.END_TIME,
  K.URL_PARAM.VIEW,
];

// Auth page
UrlParamsByRoute[K.ROUTE.SLACK_OAUTH].eventParamList = [K.URL_PARAM.CODE, K.URL_PARAM.STATE];

export default UrlParamsByRoute;
